/**
 * Order status constants and types
 * Based on https://shopify.dev/docs/api/admin-graphql/2024-04/enums/OrderDisplayFulfillmentStatus
 */

/**
 * Custom mapping of Shopify order statuses to user-friendly display values
 *
 * This mapping allows us to:
 * 1. Use more user-friendly terms (e.g., "Shipped" instead of "Fulfilled")
 * 2. Add additional statuses not in Shopify's enum (e.g., "Delivered", "Refunded")
 *
 * Using string keys to match the API response format
 */
export const ORDER_STATUS = {
  // Standard Shopify statuses with custom display names
  UNFULFILLED: 'Preparing',
  FULFILLED: 'Shipped',
  IN_PROGRESS: 'In Progress',
  ON_HOLD: 'On Hold',
  OPEN: 'Open',
  PARTIALLY_FULFILLED: 'Partially Fulfilled',
  PENDING_FULFILLMENT: 'Pending Fulfillment',
  RESTOCKED: 'Restocked',
  SCHEDULED: 'Scheduled',

  // Additional custom statuses not in Shopify's enum
  CANCELLED: 'Refunded',
  NOT_AVAILABLE: 'Not Available',
  DELIVERED: 'Delivered'
} as const;

/**
 * Valid statuses for upcoming orders
 */
export const UPCOMING_ORDER_STATUS = {
  PREPARING: 'Preparing',
  SHIPPED: 'Shipped',
  DELIVERED: 'Delivered'
} as const;

/**
 * Union type of all possible order status display values
 */
export type OrderStatus = (typeof ORDER_STATUS)[keyof typeof ORDER_STATUS];

/**
 * Type for upcoming order statuses
 */
export type UpcomingOrderStatus =
  (typeof UPCOMING_ORDER_STATUS)[keyof typeof UPCOMING_ORDER_STATUS];

/**
 * Translation keys for order status descriptions in banners
 */
export type BannerTranslationKey =
  | 'upcomingOrderBanner.preparingDescription'
  | 'upcomingOrderBanner.shippedDescription'
  | 'upcomingOrderBanner.deliveredDescription';

/**
 * Translation keys for order status descriptions in modal windows
 */
export type ModalWindowsTranslationKey =
  | 'shipNow.preparingDescription'
  | 'shipNow.shippedDescription'
  | 'shipNow.deliveredDescription'
  | 'shipNow.preparing'
  | 'shipNow.shipped'
  | 'shipNow.delivered';
